import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { IRankedResult } from '../../../Types/Docs';
import { toast } from '../../../Utils/toast';

interface RankedResultsListProps {
  results: IRankedResult[];
  'data-testid'?: string;
}

const RankedResultsList: React.FC<RankedResultsListProps> = ({
  results,
  'data-testid': testId,
}) => {
  const [expanded, setExpanded] = useState<string | false>('panel-0');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleDownload = async (docId: string, name: string) => {
    setDownloadingId(docId);
    try {
      const { downloadDocument } = await import('../../../Utils/docsApi');
      const blob = await downloadDocument(docId);

      if (!blob || blob.size === 0) {
        throw new Error('Received empty file');
      }

      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = name || 'document';
      window.document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);

      toast.success('Download started');
    } catch (error: unknown) {
      const err = error as { message?: string; response?: { data?: { message?: string } } };
      const errorMessage =
        err?.message || err?.response?.data?.message || 'Failed to download document';
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }} data-testid={testId}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Ranked Results ({results.length})
      </Typography>
      <Box>
        {results.map((result, index) => (
          <Accordion
            key={result.docId}
            expanded={expanded === `panel-${index}`}
            onChange={handleChange(`panel-${index}`)}
            sx={{ mb: 1 }}
            data-testid={`${testId}-result-${index}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                pr={2}
              >
                <Box
                  component={RouterLink}
                  to={`/${encodeURIComponent(result.name)}`}
                  onClick={(e) => e.stopPropagation()}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  minWidth={0}
                  flex={1}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover .ranked-result-name': {
                      textDecoration: 'underline',
                      color: 'primary.main',
                    },
                  }}
                  aria-label={`View ${result.name}`}
                  data-testid={`${testId}-view-link-${index}`}
                >
                  <Chip label={`#${result.rank}`} size="small" color="primary" />
                  <Typography
                    className="ranked-result-name"
                    variant="body1"
                    sx={{ fontWeight: 500 }}
                    noWrap
                  >
                    {result.name}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1} pr={2}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontStyle: 'italic' }}
                  >
                    {result.relevanceReason}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {result.summary}
                  </Typography>
                </Box>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(result.docId, result.name);
                  }}
                  disabled={downloadingId === result.docId}
                  aria-label={`Download ${result.name}`}
                  data-testid={`${testId}-download-${result.docId}`}
                >
                  {downloadingId === result.docId ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DownloadIcon />
                  )}
                </IconButton>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
};

export default RankedResultsList;
